import { Store } from '@ngrx/store';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';

import { DepartmentsType, SpinnerColor, TextSizes, TextType } from 'src/app/shared/enums';
import { IUser } from 'src/app/shared/services/user/user.type';
import {
  addUserAvatarSuccess,
  getUserPageData,
  userDescriptionUpdated,
} from 'src/app/data/store/actions/users.actions';
import {
  selectCurrentUser,
  selectUserPageData,
} from 'src/app/data/store/selectors/users.selectors';
import { befreeUrlValues } from 'src/app/shared/constants';
import { copyOnLongPress } from 'src/app/shared/utils/gestures/longPress';
import { ScrollPositionSaver } from 'src/app/shared/services/scroll-position-saver/scroll-position-saver';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { ActionSheetController, IonicModule } from '@ionic/angular';
import { getPostData } from 'src/app/data/store/actions/posts.actions';
import { UserService } from '../../services/user/user.service';
import { catchError, map, of, tap } from 'rxjs';
import { IonPageContainerComponent } from '../ion-page-container/ion-page-container.component';
import { IonPageContentComponent } from '../ion-page-container/ion-page-content/ion-page-content.component';
import { AsyncPipe } from '@angular/common';
import { ImageComponent } from '../image/image.component';
import { AvatarPickerComponent } from '../avatar-picker/avatar-picker.component';
import { TextComponent } from '../text/text.component';
import { SvgComponent } from '../svg/svg.component';
import { AddBaseUrlPipe } from '../../pipe/add-baseUrl/add-base-url.pipe';
import { FormsModule } from '@angular/forms';
import { BirthdayFormatPipe } from '../../pipe/birthday-format/birthday-format.pipe';
import { YearsOfServicePipe } from '../../pipe/years-of-service/years-of-service.pipe';
import { YearsOfServiceBadgeIconPipe } from '../../pipe/years-of-service-badge-icon/years-of-service-badge-icon.pipe';
import { ButtonComponent } from '../button/button.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@UntilDestroy()
@Component({
  selector: 'app-current-employee-card',
  templateUrl: './current-employee-card.component.html',
  styleUrls: ['./current-employee-card.component.scss'],
  imports: [
    IonPageContainerComponent,
    IonPageContentComponent,
    ImageComponent,
    AvatarPickerComponent,
    TextComponent,
    IonicModule,
    RouterLink,
    SvgComponent,
    AsyncPipe,
    AddBaseUrlPipe,
    FormsModule,
    BirthdayFormatPipe,
    YearsOfServicePipe,
    YearsOfServiceBadgeIconPipe,
    ButtonComponent,
    SpinnerComponent,
  ],
})
export class CurrentEmployeeCardComponent implements OnInit, OnDestroy {
  private _store = inject(Store);
  private _utils = inject(UtilsService);
  private _scrollPositionSaver = inject(ScrollPositionSaver);
  private readonly _activateRoute = inject(ActivatedRoute);
  private _actionSheetCtrl = inject(ActionSheetController);
  private _userService = inject(UserService);
  private _router = inject(Router);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  @Input() userId: number | undefined;

  @ViewChild('userEmail') userEmail?: ElementRef<HTMLLinkElement>;

  @ViewChild('userPhone') userPhone?: ElementRef<HTMLLinkElement>;

  @ViewChild('aboutEditRow') private _aboutEditRow?: ElementRef<HTMLElement>;

  @ViewChild('aboutTextarea', { static: false })
  private _aboutTextarea?: ElementRef<HTMLTextAreaElement>;

  public readonly TextType = TextType;

  public readonly TextSizes = TextSizes;

  public readonly DepartmentsType = DepartmentsType;

  public readonly SpinnerColor = SpinnerColor;

  public readonly maxAboutLength = 500;

  public isEditingAbout = false;

  public aboutDraft = '';

  public isSavingAbout = false;

  public isLoading = false;

  public userData: IUser | null = null;

  public currentUser$ = this._store.select(selectCurrentUser);

  currentUser: IUser | null = null;

  private _emailCopyUnsubscribe: VoidFunction = () => {
    // no-op: обработка по умолчанию
  };

  private _phoneCopyUnsubscribe: VoidFunction = () => {
    // no-op: обработка по умолчанию
  };

  ngOnInit(): void {
    this.currentUser$.pipe(untilDestroyed(this)).subscribe((currentUser) => {
      this.currentUser = currentUser;
    });

    this._activateRoute.params.pipe(untilDestroyed(this)).subscribe((routeParams) => {
      this._store.dispatch(getUserPageData({ userId: this.userId ?? routeParams['userId'] }));
    });

    this._store
      .select(selectUserPageData)
      .pipe(untilDestroyed(this))
      .subscribe((user) => {
        this.userData = user;

        setTimeout(() => {
          if (this.userEmail && this.userData!.email) {
            this._emailCopyUnsubscribe = copyOnLongPress(
              this.userEmail.nativeElement,
              this.userData!.email,
            );
          }
          if (this.userPhone && this.userData!.phoneNumber) {
            this._phoneCopyUnsubscribe = copyOnLongPress(
              this.userPhone.nativeElement,
              this.userData!.phoneNumber,
            );
          }
        }, 1000);

        if (this.userData) {
          this.currentUser$.pipe(untilDestroyed(this)).subscribe((currentUser) => {
            if (
              currentUser &&
              currentUser.id === this.userData!.id &&
              this.userData?.avatar &&
              currentUser.avatar?.previewNormal !== this.userData?.avatar?.previewNormal
            ) {
              this._store.dispatch(
                addUserAvatarSuccess({ userId: this.userData!.id, avatar: this.userData!.avatar }),
              );
            }
          });
        }
      });
  }

  ngOnDestroy(): void {
    this._emailCopyUnsubscribe();
    this._phoneCopyUnsubscribe();
  }

  get isDesktop() {
    return this._utils.isDesktop;
  }

  get departmentUrl() {
    if (this.userData?.userDepartment) {
      switch (this.userData.userDepartment.type) {
        case DepartmentsType.dept:
          return [
            '',
            befreeUrlValues.dashboard,
            befreeUrlValues.departments,
            this.userData.userDepartment.id,
            befreeUrlValues.employees,
          ].join('/');
        case DepartmentsType.shop:
          return [
            '',
            befreeUrlValues.dashboard,
            befreeUrlValues.shopList,
            this.userData.userDepartment.id,
            befreeUrlValues.employees,
          ].join('/');
        case DepartmentsType.group:
          return [
            '',
            befreeUrlValues.dashboard,
            befreeUrlValues.groups,
            this.userData.userDepartment.id,
            befreeUrlValues.employees,
          ].join('/');
        case DepartmentsType.region:
          return [
            '',
            befreeUrlValues.dashboard,
            befreeUrlValues.regions,
            this.userData.userDepartment.id,
            befreeUrlValues.employees,
          ].join('/');
        default:
          return '';
      }
    }
    return '';
  }

  get employeeName() {
    if (!this.userData) {
      return '';
    }

    return [this.userData.firstName, this.userData.lastName].filter(Boolean).join(' ');
  }

  get employeeStatus() {
    return this.userData?.state ?? '';
  }

  get isOwnProfile(): boolean {
    return !!(this.currentUser && this.userData && this.currentUser.id === this.userData.id);
  }

  get canReport(): boolean {
    return !!this.userData && !this.isOwnProfile;
  }

  get showAboutSection(): boolean {
    if (!this.userData) {
      return false;
    }
    const hasText = !!(this.userData.description ?? '').trim();
    return hasText || this.isOwnProfile;
  }

  get aboutDisplayText(): string {
    return this.userData?.description ?? '';
  }

  public startEditAbout(): void {
    if (!this.userData) {
      return;
    }
    this.aboutDraft = (this.userData.description ?? '').slice(0, this.maxAboutLength);
    this.isEditingAbout = true;
    this._changeDetectorRef.detectChanges();
    this._focusAboutTextareaAtEnd();
    this._scrollAboutEditRowIntoView();
  }

  private _runAfterLayout(callback: () => void): void {
    callback();
    requestAnimationFrame(() => requestAnimationFrame(callback));
  }

  private _scrollAboutEditRowIntoView(): void {
    const aboutEditRowElement = this._aboutEditRow?.nativeElement;
    if (!aboutEditRowElement) {
      return;
    }
    const scrollAboutEditRowIntoView = (): void =>
      aboutEditRowElement.scrollIntoView({ block: 'end', inline: 'nearest', behavior: 'auto' });
    this._runAfterLayout(scrollAboutEditRowIntoView);

    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null;
    if (visualViewport) {
      const handleVisualViewportResize = (): void => scrollAboutEditRowIntoView();
      visualViewport.addEventListener('resize', handleVisualViewportResize);
      window.setTimeout(() => {
        visualViewport.removeEventListener('resize', handleVisualViewportResize);
      }, 800);
    }
  }

  private _focusAboutTextareaAtEnd(): void {
    const aboutTextareaElement = this._aboutTextarea?.nativeElement;
    if (!aboutTextareaElement) {
      return;
    }
    aboutTextareaElement.focus();
    const textLength = aboutTextareaElement.value.length;
    try {
      aboutTextareaElement.setSelectionRange(textLength, textLength);
    } catch {
      // no-op: обработка по умолчанию
    }
    this._runAfterLayout(() => {
      aboutTextareaElement.scrollTop = aboutTextareaElement.scrollHeight;
    });
  }

  public saveAbout(): void {
    if (!this.userData || this.isSavingAbout) {
      return;
    }
    const trimmed = this.aboutDraft.trim().slice(0, this.maxAboutLength);
    const current = (this.userData.description ?? '').trim();
    if (trimmed === current) {
      this.isEditingAbout = false;
      return;
    }
    const userId = this.userData.id;
    this.isSavingAbout = true;
    this._userService
      .updateUserDescription(this.userData, trimmed)
      .pipe(
        tap(() => {
          this._store.dispatch(userDescriptionUpdated({ userId, description: trimmed }));
          this.isEditingAbout = false;
          this.isSavingAbout = false;
        }),
        catchError((error) => {
          this.isSavingAbout = false;
          this._utils.openNotification(
            error instanceof Error ? error.message : 'Не удалось сохранить раздел «Обо мне»',
            true,
          );
          return of();
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  public blockUser() {
    if (!this.userData) {
      return;
    }

    this._userService
      .blockUser(this.userData.id)
      .pipe(
        map(async () => {
          const toast = await this._actionSheetCtrl.create({
            header: 'Вы заблокировали пользователя. Вы больше не будете видеть его публикации.',
            buttons: [
              {
                text: 'OK',
                role: 'confirm',
                handler: () => {
                  this._store.dispatch(getPostData({}));
                  this._router.navigate(['/']);
                },
              },
            ],
          });
          await toast.present();
        }),
        catchError(() => of()),
      )
      .subscribe(() => {
        // no-op: обработка по умолчанию
      });
  }

  public clearScrollPositions() {
    this._scrollPositionSaver.deletePositions();
  }
}
